import { useCallback, useMemo, useRef, useState } from "react";
import { EMPTY_EDGES } from "../../core";
import { classifyCanvasNode, isBackingEntityKind } from "../../core/nodeKind";
import { buildNoteNodes, buildNoteConnectorEdges, isNoteNodeId, noteIdFromNodeId, noteNodeId } from "../../core/notes";
/**
 * The sticky-note layer of the draft editor (ADR 0008). Owns everything the
 * canvas needs to author notes — built note nodes/edges, a transient geometry
 * override, link mode — and wraps the editor's base canvas handlers so the
 * editor composes a layer it otherwise knows nothing about (no `note-` id
 * prefixes, geometry overrides or link mode leak into the component body).
 *
 * Notes are appended AFTER layout at absolute geometry (ELK never sees them);
 * connectors skip orphan targets. The override keeps a note where the user is
 * dragging/resizing across mid-gesture re-renders (controlled RF) and is
 * cleared whenever the persisted `notes` prop changes (optimistic write /
 * refetch / undo), which then becomes truth.
 */ export function useNoteLayer({ notes, selectedNodeIds, displayNodes, onUpdateNote, onPlaceNote, onRetargetNote, onDeleteNote, onNoteSelectChanges, base, clearGuides }) {
    const [noteGeoOverride, setNoteGeoOverride] = useState(()=>new Map());
    // Lane bands (y + height per lane) derived from the laid-out nodes — the
    // input for notes riding lane shifts (see `laneShiftDelta` below).
    const laneBands = useMemo(()=>{
        const bands = new Map();
        for (const n of displayNodes){
            if (n.type !== "flowSwimlane") continue;
            bands.set(n.id, {
                y: n.position.y,
                height: n.data?.height ?? 0
            });
        }
        return bands;
    }, [
        displayNodes
    ]);
    // Track the previous `notes` in STATE, not a ref: under StrictMode/concurrent
    // rendering the render runs twice and a ref mutated in the discarded pass
    // would make the second pass see `prev === notes` and skip the reset —
    // leaving the override stuck at the dragged position, so undo/refetch couldn't
    // move the note back. State-for-prev is the React-sanctioned "adjust state on
    // prop change" pattern: it only advances when the render commits.
    //
    // `laneBandBaseline` snapshots the lane bands at the moment the persisted
    // `notes` became truth — note geometry is absolute, so a note's rendered Y is
    // `geometry.y + (bandNow − bandBaseline)` of the lane band it sits in. That
    // makes notes ride lane shifts (a lane growing during a card drag pushes its
    // neighbours, and the notes over them) exactly like non-dragged cards do,
    // as a PURE derivation — no incremental override writes that a StrictMode
    // double-render could double-apply.
    const [prevNotes, setPrevNotes] = useState(notes);
    const [laneBandBaseline, setLaneBandBaseline] = useState(laneBands);
    if (prevNotes !== notes) {
        setPrevNotes(notes);
        setLaneBandBaseline(laneBands);
        if (noteGeoOverride.size > 0) setNoteGeoOverride(new Map());
    } else if (laneBandBaseline.size !== laneBands.size || [
        ...laneBands.keys()
    ].some((id)=>!laneBandBaseline.has(id))) {
        // The lane SET changed (structural edit added/removed a lane) — the old
        // baseline no longer describes this canvas; re-anchor instead of shifting
        // notes by unrelated reflow deltas.
        setLaneBandBaseline(laneBands);
    }
    // Delta of the lane band a note (identified by its baseline-coordinates
    // center Y) sits in — or the nearest band when it floats in a gap.
    const laneShiftDelta = useCallback((centerY)=>{
        let best = null;
        for (const [id, base] of laneBandBaseline){
            const now = laneBands.get(id);
            if (!now) continue;
            const dist = centerY < base.y ? base.y - centerY : centerY > base.y + base.height ? centerY - (base.y + base.height) : 0;
            if (!best || dist < best.dist) best = {
                dist,
                delta: now.y - base.y
            };
        }
        return best?.delta ?? 0;
    }, [
        laneBandBaseline,
        laneBands
    ]);
    // Link mode: while a note is "linking", clicking a node toggles it in the
    // note's `targets`. Tied to selection — auto-exits when that note is no
    // longer selected (its toolbar, and thus the link toggle, is gone).
    const [linkingNoteId, setLinkingNoteId] = useState(null);
    if (linkingNoteId && !selectedNodeIds.has(noteNodeId(linkingNoteId))) {
        setLinkingNoteId(null);
    }
    const toggleNoteLink = useCallback((noteId)=>setLinkingNoteId((prev)=>prev === noteId ? null : noteId), []);
    // Ref-stabilize the persist callbacks so `noteCallbacks` — and thus the note
    // nodes that embed them in `data` — keep a stable identity even when the
    // consumer passes fresh function identities each render. Without this, every
    // render rebuilds the note nodes; react-flow re-adopts them and, since notes
    // carry no `measured`/handles, wipes their `internals.handleBounds`. Its
    // marquee hit-test (`getNodesInside`) then force-selects any node with no
    // handle bounds, so box-selecting a note's linked step wrongly selects the
    // note too. Gate only on callback PRESENCE so identity churn never rebuilds.
    const onUpdateNoteRef = useRef(onUpdateNote);
    onUpdateNoteRef.current = onUpdateNote;
    const onPlaceNoteRef = useRef(onPlaceNote);
    onPlaceNoteRef.current = onPlaceNote;
    const onDeleteNoteRef = useRef(onDeleteNote);
    onDeleteNoteRef.current = onDeleteNote;
    const canUpdateNote = !!onUpdateNote;
    const canPlaceNote = !!onPlaceNote;
    const canDeleteNote = !!onDeleteNote;
    const canToggleLink = !!onRetargetNote;
    const noteCallbacks = useMemo(()=>({
            onTextChange: canUpdateNote ? (id, text)=>onUpdateNoteRef.current?.(id, {
                    text
                }) : undefined,
            onResizeEnd: canPlaceNote ? (noteId, geometry)=>onPlaceNoteRef.current?.(noteId, geometry) : undefined,
            onDelete: canDeleteNote ? (id)=>onDeleteNoteRef.current?.(id) : undefined,
            onCategoryChange: canUpdateNote ? (id, category)=>onUpdateNoteRef.current?.(id, {
                    category
                }) : undefined,
            onToggleLink: canToggleLink ? toggleNoteLink : undefined
        }), [
        canUpdateNote,
        canPlaceNote,
        canDeleteNote,
        canToggleLink,
        toggleNoteLink
    ]);
    const noteNodes = useMemo(()=>{
        const built = buildNoteNodes(notes ?? [], "edit", noteCallbacks);
        return built.map((n)=>{
            const ov = noteGeoOverride.get(n.data.noteId);
            // A position override (live note drag) is already in current canvas
            // coordinates — the lane-shift delta only applies to the persisted
            // geometry, which is anchored at the baseline bands.
            const y = ov?.y !== undefined ? ov.y : n.position.y + laneShiftDelta(n.position.y + (n.height ?? 0) / 2);
            const width = ov?.width ?? n.width;
            const height = ov?.height ?? n.height;
            return {
                ...n,
                position: {
                    x: ov?.x ?? n.position.x,
                    y
                },
                width,
                height,
                measured: {
                    width,
                    height
                },
                data: {
                    ...n.data,
                    isLinking: linkingNoteId === n.data.noteId
                }
            };
        });
    }, [
        notes,
        noteCallbacks,
        noteGeoOverride,
        linkingNoteId,
        laneShiftDelta
    ]);
    const noteEdges = useMemo(()=>{
        if (!notes?.length) return EMPTY_EDGES;
        const presentIds = new Set(displayNodes.map((n)=>n.id));
        return buildNoteConnectorEdges(notes, presentIds);
    }, [
        notes,
        displayNodes
    ]);
    // Drive note selection from the editor's selection model. Note nodes are
    // rebuilt from props each render, so RF's own `select` change never sticks —
    // without this the highlight + inline toolbar flash off on the next render.
    const selectedNoteNodes = useMemo(()=>noteNodes.map((n)=>{
            const isSelected = selectedNodeIds.has(n.id);
            return !!n.selected === isSelected ? n : {
                ...n,
                selected: isSelected
            };
        }), [
        noteNodes,
        selectedNodeIds
    ]);
    // Selected notes move WITH a card drag (react-flow drags the whole
    // selection; the note's live position lands in the override below and gets
    // persisted on drop). The reverse is kept off: a note-primary drag leaves
    // selected cards in place, because card movement needs the lane drag
    // lifecycle (reflow + settle + commit) that note drags deliberately skip.
    const dragPrimaryIsNoteRef = useRef(null);
    // Track live note geometry from RF changes (drag/resize) into the override.
    // Only resize-driven dimension changes count: NodeResizer stamps them
    // `resizing: true`, whereas the measurement pass (`updateNodeInternals`)
    // emits unflagged `dimensions` changes carrying the DOM-measured size. In
    // controlled mode those race the live resize and reset the override every
    // frame (no live grow, and a stale size committed on release).
    const onNodesChange = useCallback((incoming)=>{
        const changes = dragPrimaryIsNoteRef.current === true ? incoming.filter((c)=>c.type !== "position" || isNoteNodeId(c.id)) : incoming;
        const noteSelects = changes.filter((c)=>c.type === "select" && isNoteNodeId(c.id));
        if (noteSelects.length > 0) {
            onNoteSelectChanges?.(noteSelects.map((c)=>({
                    id: c.id,
                    selected: c.selected
                })));
        }
        const noteChanges = changes.filter((c)=>"id" in c && isNoteNodeId(c.id) && (c.type === "position" || c.type === "dimensions" && c.resizing === true));
        if (noteChanges.length > 0) {
            setNoteGeoOverride((prev)=>{
                const next = new Map(prev);
                for (const c of noteChanges){
                    const id = noteIdFromNodeId(c.id);
                    const cur = next.get(id) ?? {};
                    if (c.type === "position" && c.position) {
                        next.set(id, {
                            ...cur,
                            x: c.position.x,
                            y: c.position.y
                        });
                    } else if (c.type === "dimensions" && c.dimensions) {
                        next.set(id, {
                            ...cur,
                            width: c.dimensions.width,
                            height: c.dimensions.height
                        });
                    }
                }
                return next;
            });
        }
        base.onNodesChange(changes);
    }, [
        base,
        onNoteSelectChanges
    ]);
    // Notes sit outside the lane system (ADR 0008). The editable-flow drag
    // handler reflows every lane on each frame, which would drag linked/adjacent
    // nodes (e.g. a decision) along with the note — skip it for note drags; the
    // note's own geometry is tracked by `onNodesChange`.
    const onNodeDrag = useCallback((event, node, dragged)=>{
        if (isNoteNodeId(node.id)) return;
        base.onNodeDrag(event, node, dragged);
    }, [
        base
    ]);
    // Persist a note on drag-stop; non-note drags fall through to the base hook,
    // then persist the lane-shift deltas the drag produced: note geometry is
    // absolute, so without a write-back the next `notes` refetch would snap the
    // notes away from the content they rode along with.
    const onNodeDragStop = useCallback((event, node, dragged)=>{
        dragPrimaryIsNoteRef.current = null;
        if (isNoteNodeId(node.id)) {
            if (onPlaceNote) {
                const data = node.data;
                // Geometry lives on the RF node; the persisted note's geometry is the
                // fallback (the node always carries one, so the fallback is defensive).
                const fallback = notes?.find((nn)=>nn._id === data.noteId)?.geometry;
                onPlaceNote(data.noteId, {
                    x: node.position.x,
                    y: node.position.y,
                    width: node.width ?? fallback?.width ?? 0,
                    height: node.height ?? fallback?.height ?? 0
                });
                clearGuides();
            }
            return;
        }
        base.onNodeDragStop(event, node, dragged);
        if (onPlaceNote && notes) {
            for (const note of notes){
                const ov = noteGeoOverride.get(note._id);
                if (ov?.x !== undefined || ov?.y !== undefined) {
                    // The note was dragged along as part of this selection — its live
                    // position sits in the override (current coordinates); persist it
                    // like the note's own drop would.
                    onPlaceNote(note._id, {
                        x: ov.x ?? note.geometry.x,
                        y: ov.y ?? note.geometry.y,
                        width: ov.width ?? note.geometry.width,
                        height: ov.height ?? note.geometry.height
                    });
                    continue;
                }
                // Baseline-anchored note: persist the lane-shift delta the drag
                // produced, so the next notes refetch doesn't snap it away from the
                // content it rode along with.
                const delta = laneShiftDelta(note.geometry.y + note.geometry.height / 2);
                if (Math.abs(delta) < 0.5) continue;
                onPlaceNote(note._id, {
                    x: note.geometry.x,
                    y: note.geometry.y + delta,
                    width: ov?.width ?? note.geometry.width,
                    height: ov?.height ?? note.geometry.height
                });
            }
        }
    }, [
        base,
        onPlaceNote,
        notes,
        clearGuides,
        noteGeoOverride,
        laneShiftDelta
    ]);
    // Keep notes out of the lane/structural drag lifecycle (ADR 0008). Their
    // drag-stop never calls the base hook's `onNodeDragStop`, so letting the hook
    // flag the drag here would strand `isDragging` and freeze structural rebuilds.
    const onNodeDragStart = useCallback((event, node, nodes)=>{
        dragPrimaryIsNoteRef.current = isNoteNodeId(node.id);
        if (isNoteNodeId(node.id)) return;
        base.onNodeDragStart(event, node, nodes);
    }, [
        base
    ]);
    // In link mode, a click on a linkable node toggles it in the active note's
    // `targets` (drawing/removing the connector) instead of selecting it; link
    // mode stays on so several nodes can be linked in a row. Other clicks fall
    // through to normal selection.
    const onNodeClick = useCallback((event, node)=>{
        if (linkingNoteId && onRetargetNote && isBackingEntityKind(classifyCanvasNode(node))) {
            const note = notes?.find((n)=>n._id === linkingNoteId);
            if (note) {
                const next = note.targets.includes(node.id) ? note.targets.filter((id)=>id !== node.id) : [
                    ...note.targets,
                    node.id
                ];
                onRetargetNote(linkingNoteId, next);
            }
            return;
        }
        base.onNodeClick(event, node);
    }, [
        linkingNoteId,
        onRetargetNote,
        notes,
        base
    ]);
    // Clicking empty canvas finishes link mode (and clears selection as usual).
    const onPaneClick = useCallback(()=>{
        if (linkingNoteId) setLinkingNoteId(null);
        base.onPaneClick();
    }, [
        linkingNoteId,
        base
    ]);
    // Notes have no node context menu (the target resolves to null downstream and
    // the menu is suppressed). Select on right-click so the inline toolbar
    // (recolour/delete) surfaces, matching left-click.
    const onNoteContextMenuSelect = useCallback((event, node)=>{
        base.onNodeClick(event, node);
    }, [
        base
    ]);
    const handlers = useMemo(()=>({
            onNodesChange,
            onNodeDrag,
            onNodeDragStop,
            onNodeDragStart,
            onNodeClick,
            onPaneClick,
            onNoteContextMenuSelect
        }), [
        onNodesChange,
        onNodeDrag,
        onNodeDragStop,
        onNodeDragStart,
        onNodeClick,
        onPaneClick,
        onNoteContextMenuSelect
    ]);
    return {
        noteNodes: selectedNoteNodes,
        noteEdges,
        linkingNoteId,
        handlers
    };
}

//# sourceMappingURL=useNoteLayer.js.map